/*
 * STORE LOCATOR (sections/store-locator.liquid)
 * Mapa Leaflet + mapa gris de Esri (sin clave de API; se carga al llegar a la sección), lista filtrada por la zona visible del mapa y ordenada por
 * distancia, buscador (Nominatim / OpenStreetMap), "Mi ubicación" y ficha de cada tienda.
 */
(() => {
  if (window.customElements.get('store-locator')) return;

  const LEAFLET = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/';
  const TILES = 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}';
  const LABELS = 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}';
  const ATTRIBUTION = 'Tiles &copy; Esri &mdash; Esri, HERE, Garmin, &copy; OpenStreetMap contributors';
  const GEOCODER = 'https://nominatim.openstreetmap.org/search';

  let leafletPromise = null;

  const loadLeaflet = () => {
    if (window.L) return Promise.resolve(window.L);
    if (leafletPromise) return leafletPromise;
    leafletPromise = new Promise((resolve, reject) => {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = `${LEAFLET}leaflet.min.css`;
      document.head.appendChild(css);
      const script = document.createElement('script');
      script.src = `${LEAFLET}leaflet.min.js`;
      script.onload = () => resolve(window.L);
      script.onerror = reject;
      document.head.appendChild(script);
    });
    return leafletPromise;
  };

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const distanceKm = (a, b) => {
    const rad = (deg) => (deg * Math.PI) / 180;
    const dLat = rad(b.lat - a.lat);
    const dLng = rad(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 12742 * Math.asin(Math.sqrt(h));
  };

  // Búsqueda de coordenadas (con caché en el navegador); Nominatim pide máx. 1 petición por segundo
  let geocodeQueue = Promise.resolve();
  const geocode = (query, countries) => {
    const key = `sl-geo:${countries}:${query.toLowerCase()}`;
    try {
      const cached = localStorage.getItem(key);
      if (cached) return Promise.resolve(JSON.parse(cached));
    } catch (error) { /* sin localStorage */ }

    const run = () => {
      const params = new URLSearchParams({ q: query, format: 'json', limit: '1', 'accept-language': document.documentElement.lang || 'en' });
      if (countries) params.set('countrycodes', countries);
      return fetch(`${GEOCODER}?${params}`)
        .then((response) => (response.ok ? response.json() : []))
        .then((results) => {
          const hit = results && results[0];
          const point = hit ? { lat: parseFloat(hit.lat), lng: parseFloat(hit.lon) } : null;
          try { if (point) localStorage.setItem(key, JSON.stringify(point)); } catch (error) { /* sin localStorage */ }
          return point;
        })
        .catch(() => null);
    };

    const result = geocodeQueue.then(run);
    geocodeQueue = result.then(() => new Promise((resolve) => setTimeout(resolve, 1100)));
    return result;
  };

  const ICONS = {
    pin: '<svg viewBox="0 0 34 44" aria-hidden="true"><path fill="currentColor" d="M17 0C7.6 0 0 7.4 0 16.6 0 29 17 44 17 44s17-15 17-27.4C34 7.4 26.4 0 17 0Z"/><circle cx="17" cy="16.5" r="6" fill="#fff"/></svg>',
    directions: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M21.7 11.3 12.7 2.3a1 1 0 0 0-1.4 0l-9 9a1 1 0 0 0 0 1.4l9 9a1 1 0 0 0 1.4 0l9-9a1 1 0 0 0 0-1.4ZM14 14.5V12h-4v3H8v-4a1 1 0 0 1 1-1h5V7.5l3.5 3.5-3.5 3.5Z"/></svg>',
    phone: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1L6.6 10.8Z"/></svg>',
    web: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8h5Z"/></svg>',
    close: '<svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true"><path stroke="currentColor" stroke-width="2" d="m5 5 14 14M19 5 5 19"/></svg>',
  };

  class StoreLocator extends HTMLElement {
    connectedCallback() {
      this.stores = this.parse('[data-sl-stores]', []).map((store, index) => ({
        ...store,
        key: String(store.id || index),
        lat: Number.isFinite(parseFloat(store.lat)) ? parseFloat(store.lat) : null,
        lng: Number.isFinite(parseFloat(store.lng)) ? parseFloat(store.lng) : null,
      }));
      this.strings = this.parse('[data-sl-strings]', {});
      this.units = this.dataset.units === 'mi' ? 'mi' : 'km';
      this.pageSize = parseInt(this.dataset.pageSize, 10) || 5;
      this.countries = this.dataset.countries || '';
      this.filterByMap = this.dataset.filterByMap !== 'false';
      this.origin = null;
      this.page = 1;
      this.activeKey = null;

      this.listEl = this.querySelector('[data-sl-list]');
      this.moreEl = this.querySelector('[data-sl-more]');
      this.statusEl = this.querySelector('[data-sl-status]');
      this.detailEl = this.querySelector('[data-sl-detail]');
      this.mapEl = this.querySelector('[data-sl-map]');

      this.querySelector('[data-sl-search]').addEventListener('submit', (event) => {
        event.preventDefault();
        this.search(event.currentTarget.elements.q.value.trim());
      });
      this.querySelector('[data-sl-locate]').addEventListener('click', () => this.locate());
      this.moreEl.addEventListener('click', () => { this.page += 1; this.renderList(); });

      // El mapa se carga al acercarse a la sección
      const start = () => this.init().catch(() => {});
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) { observer.disconnect(); start(); }
        }, { rootMargin: '400px' });
        observer.observe(this);
      } else {
        start();
      }
    }

    parse(selector, fallback) {
      try { return JSON.parse(this.querySelector(selector).textContent); } catch (error) { return fallback; }
    }

    async init() {
      const L = await loadLeaflet();
      this.L = L;
      this.map = L.map(this.mapEl, { scrollWheelZoom: false, zoomControl: true });
      L.tileLayer(TILES, { attribution: ATTRIBUTION, maxZoom: 16 }).addTo(this.map);
      L.tileLayer(LABELS, { maxZoom: 16 }).addTo(this.map);
      // Zoom con la rueda solo después de hacer clic en el mapa (si no, atrapa el scroll de la página)
      this.map.on('click', () => this.map.scrollWheelZoom.enable());
      this.mapEl.addEventListener('mouseleave', () => this.map.scrollWheelZoom.disable());

      this.markers = new Map();
      this.stores.filter((store) => store.lat !== null).forEach((store) => this.addMarker(store));

      const [lat, lng] = (this.dataset.center || '').split(',').map(parseFloat);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        this.map.setView([lat, lng], parseInt(this.dataset.zoom, 10) || 6);
      } else {
        this.fitAll();
      }

      this.map.on('moveend', () => {
        if (this.detailEl.hidden) { this.page = 1; this.renderList(); }
      });
      this.renderList();

      // Tiendas sin coordenadas: se buscan por su dirección
      for (const store of this.stores.filter((entry) => entry.lat === null && entry.address)) {
        const point = await geocode(store.address.replace(/\n/g, ', '), this.countries);
        if (!point) continue;
        Object.assign(store, point);
        this.addMarker(store);
        this.renderList();
      }
    }

    addMarker(store) {
      const icon = this.L.divIcon({ className: 'sl-pin', html: ICONS.pin, iconSize: null });
      const marker = this.L.marker([store.lat, store.lng], { icon, title: store.name, keyboard: true }).addTo(this.map);
      marker.on('click', () => this.openStore(store));
      this.markers.set(store.key, marker);
    }

    fitAll() {
      const points = this.stores.filter((store) => store.lat !== null).map((store) => [store.lat, store.lng]);
      if (points.length === 0) { this.map.setView([40.4, -3.7], 5); return; }
      if (points.length === 1) { this.map.setView(points[0], 13); return; }
      this.map.fitBounds(points, { padding: [40, 40] });
    }

    formatDistance(km) {
      const value = this.units === 'mi' ? km * 0.621371 : km;
      return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${this.units}`;
    }

    visibleStores() {
      const located = this.stores.filter((store) => store.lat !== null);
      const bounds = this.map && this.map.getBounds();
      const inView = this.filterByMap && bounds ? located.filter((store) => bounds.contains([store.lat, store.lng])) : located;
      const center = this.origin || (this.map ? { lat: this.map.getCenter().lat, lng: this.map.getCenter().lng } : null);
      return inView
        .map((store) => ({ store, distance: center ? distanceKm(center, store) : 0 }))
        .sort((a, b) => a.distance - b.distance);
    }

    renderList() {
      const results = this.visibleStores();
      const shown = results.slice(0, this.page * this.pageSize);

      this.statusEl.hidden = results.length > 0;
      if (results.length === 0) {
        this.statusEl.innerHTML = `${escapeHtml(this.strings.noResults)} <button type="button">${escapeHtml(this.strings.showAll)}</button>`;
        this.statusEl.querySelector('button').addEventListener('click', () => { this.origin = null; this.fitAll(); });
      }

      this.listEl.innerHTML = shown.map(({ store, distance }) => `
        <li>
          <button type="button" class="store-locator__card ${store.key === this.activeKey ? 'is-active' : ''}" data-key="${escapeHtml(store.key)}">
            ${store.logo ? `<img class="store-locator__card-logo" src="${escapeHtml(store.logo)}" alt="${escapeHtml(store.name)}" loading="lazy">` : `<span class="store-locator__card-name">${escapeHtml(store.name)}</span>`}
            ${store.address ? `<span class="store-locator__card-address">${escapeHtml(store.address.replace(/\n/g, ', '))}</span>` : ''}
            ${this.origin ? `<span class="store-locator__card-distance">${this.formatDistance(distance)}</span>` : ''}
          </button>
        </li>`).join('');

      this.listEl.querySelectorAll('[data-key]').forEach((button) => {
        const store = this.stores.find((entry) => entry.key === button.dataset.key);
        button.addEventListener('click', () => this.openStore(store));
        button.addEventListener('mouseenter', () => this.highlight(store.key, true));
        button.addEventListener('mouseleave', () => this.highlight(store.key, false));
      });

      this.moreEl.hidden = shown.length >= results.length;
    }

    highlight(key, on) {
      const element = this.markers.get(key)?.getElement();
      if (element) element.classList.toggle('is-active', on || key === this.activeKey);
    }

    openStore(store) {
      if (!store) return;
      if (this.activeKey) this.highlight(this.activeKey, false);
      if (this.detailEl.hidden) this.previousView = { center: this.map.getCenter(), zoom: this.map.getZoom() };
      this.activeKey = store.key;
      this.highlight(store.key, true);

      const directions = `https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}`;
      const website = store.website ? store.website.replace(/^https?:\/\//, '').replace(/\/$/, '') : '';
      const hours = String(store.hours || '').split('\n').map((line) => line.trim()).filter(Boolean);

      this.detailEl.innerHTML = `
        <button type="button" class="store-locator__close" aria-label="${escapeHtml(this.strings.close)}">${ICONS.close}</button>
        ${store.image ? `<img class="store-locator__detail-image" src="${escapeHtml(store.image)}" alt="${escapeHtml(store.name)}">` : ''}
        ${store.logo ? `<img class="store-locator__detail-logo" src="${escapeHtml(store.logo)}" alt="${escapeHtml(store.name)}">` : ''}
        <h3 class="store-locator__detail-name h4">${escapeHtml(store.name)}</h3>
        ${store.address ? `<p class="store-locator__detail-address">${escapeHtml(store.address)}</p>` : ''}
        <ul class="store-locator__actions" role="list">
          <li><a class="store-locator__action" href="${directions}" target="_blank" rel="noopener">${ICONS.directions}${escapeHtml(this.strings.directions)}</a></li>
          ${store.phone ? `<li><a class="store-locator__action" href="tel:${escapeHtml(store.phone.replace(/\s/g, ''))}">${ICONS.phone}${escapeHtml(store.phone)}</a></li>` : ''}
          ${website ? `<li><a class="store-locator__action" href="${escapeHtml(store.website)}" target="_blank" rel="noopener">${ICONS.web}${escapeHtml(website)}</a></li>` : ''}
        </ul>
        ${hours.length ? `<div><p class="store-locator__hours-title h5">${escapeHtml(this.strings.openingHours)}</p><ul class="store-locator__hours" role="list">${hours.map((line) => `<li>${escapeHtml(line)}</li>`).join('')}</ul></div>` : ''}
      `;
      this.detailEl.querySelector('.store-locator__close').addEventListener('click', () => this.closeStore());

      this.listEl.hidden = true;
      this.moreEl.hidden = true;
      this.statusEl.hidden = true;
      this.detailEl.hidden = false;
      this.map.setView([store.lat, store.lng], Math.max(this.map.getZoom(), 15));

      // En móvil la ficha está debajo del mapa
      if (window.matchMedia('(max-width: 999px)').matches) {
        this.detailEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    closeStore() {
      const key = this.activeKey;
      this.activeKey = null;
      this.highlight(key, false);
      this.detailEl.hidden = true;
      this.listEl.hidden = false;
      if (this.previousView) this.map.setView(this.previousView.center, this.previousView.zoom);
      this.renderList();
    }

    setOrigin(point) {
      this.origin = point;
      if (this.youMarker) this.youMarker.remove();
      const icon = this.L.divIcon({ className: 'sl-you', iconSize: null });
      this.youMarker = this.L.marker([point.lat, point.lng], { icon, title: this.strings.you, interactive: false }).addTo(this.map);
      if (!this.detailEl.hidden) this.closeStore();

      // Zoom hasta que se vean al menos las tiendas de la primera página (o la más cercana)
      const nearest = this.stores
        .filter((store) => store.lat !== null)
        .map((store) => ({ store, distance: distanceKm(point, store) }))
        .sort((a, b) => a.distance - b.distance)
        .slice(0, this.pageSize);
      if (nearest.length) {
        const bounds = this.L.latLngBounds([[point.lat, point.lng], ...nearest.slice(0, 3).map(({ store }) => [store.lat, store.lng])]);
        this.map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
      } else {
        this.map.setView([point.lat, point.lng], 12);
      }
      this.page = 1;
      this.renderList();
    }

    async search(query) {
      if (!query || !this.map) return;
      const point = await geocode(query, this.countries);
      if (!point) {
        this.statusEl.hidden = false;
        this.statusEl.textContent = this.strings.notFound;
        return;
      }
      this.setOrigin(point);
    }

    locate() {
      if (!navigator.geolocation || !this.map) return;
      navigator.geolocation.getCurrentPosition(
        (position) => this.setOrigin({ lat: position.coords.latitude, lng: position.coords.longitude }),
        () => {
          this.statusEl.hidden = false;
          this.statusEl.textContent = this.strings.locationError;
        },
        { enableHighAccuracy: false, timeout: 10000 },
      );
    }
  }

  window.customElements.define('store-locator', StoreLocator);
})();
